(() => {
  if (customElements.get('alcyon-delivery-preference')) return;
  const codes = {
    'Signature required (card if not home)': 'SIGNATURE_REQUIRED',
    'Authority to leave in a safe place (no signature)': 'AUTHORITY_TO_LEAVE'
  };
  let queue = Promise.resolve();
  let latest = null;
  let savedAttributes = null;
  const replay = new WeakSet();
  const pending = new WeakSet();
  const hosts = () => Array.from(document.querySelectorAll('alcyon-delivery-preference'));
  const setStatus = (host, message) => {
    const status = host.querySelector('[data-delivery-status]');
    if (status) status.textContent = message;
  };
  const applyChoice = (host, value) => {
    host.querySelector('[data-delivery-choice]').value = value;
    host.querySelector('[data-delivery-code]').value = codes[value] || '';
  };
  const attributesFor = (host) => {
    const form = document.getElementById(host.dataset.formId);
    const attributes = {};
    if (form) {
      for (const field of Array.from(form.elements)) {
        const match = /^attributes\[(.+)\]$/.exec(field.name || '');
        if (match && !field.disabled && !((field.type === 'checkbox' || field.type === 'radio') && !field.checked)) {
          attributes[match[1]] = field.value;
        }
      }
    }
    const value = host.querySelector('[data-delivery-choice]').value;
    attributes['Special delivery instructions'] = value;
    attributes['Delivery signature preference'] = codes[value] || '';
    attributes['Additional delivery details'] = host.querySelector('[data-delivery-details]').value;
    return attributes;
  };
  const save = (attributes) => {
    queue = queue.catch(() => {}).then(async () => {
      const base = window.routes?.cart_update_url || ((window.Shopify?.routes?.root || '/') + 'cart/update');
      const url = base.endsWith('.js') ? base : base + '.js';
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes }),
        keepalive: true,
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Delivery preference save failed');
      const cart = await response.json();
      if (cart.attributes?.['Delivery signature preference'] !== attributes['Delivery signature preference'] ||
          cart.attributes?.['Special delivery instructions'] !== attributes['Special delivery instructions'] ||
          (cart.attributes?.['Additional delivery details'] || '') !== attributes['Additional delivery details']) {
        throw new Error('Delivery preference readback mismatch');
      }
      savedAttributes = { ...attributes };
      return cart;
    });
    return queue;
  };
  class DeliveryPreference extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      if (latest) {
        applyChoice(this, latest.preference);
        this.querySelector('[data-delivery-details]').value = latest.details;
      }
      if (!latest && codes[this.querySelector('[data-delivery-choice]').value]) {
        latest = { preference: this.querySelector('[data-delivery-choice]').value, details: this.querySelector('[data-delivery-details]').value };
        save(attributesFor(this)).catch(() => setStatus(this, this.dataset.errorMessage));
      }
      this.addEventListener('input', event => {
        if (!event.target.matches('[data-delivery-details]')) return;
        clearTimeout(this.detailsTimer);
        const value = this.querySelector('[data-delivery-choice]').value;
        latest = { preference: value, details: event.target.value };
        for (const other of hosts()) {
          if (other !== this) other.querySelector('[data-delivery-details]').value = latest.details;
        }
        if (!codes[value]) return;
        setStatus(this, this.dataset.savingMessage);
        this.detailsTimer = setTimeout(() => {
          save(attributesFor(this)).then(() => setStatus(this, '')).catch(() => setStatus(this, this.dataset.errorMessage));
        }, 300);
      });
      this.addEventListener('change', () => {
        clearTimeout(this.detailsTimer);
        const value = this.querySelector('[data-delivery-choice]').value;
        if (!codes[value]) return;
        this.querySelector('[data-delivery-choice]').setAttribute('aria-invalid', 'false');
        this.querySelector('[data-delivery-code]').value = codes[value];
        latest = { preference: value, details: this.querySelector('[data-delivery-details]').value };
        for (const other of hosts()) {
          if (other !== this) {
            applyChoice(other, value);
            other.querySelector('[data-delivery-details]').value = latest.details;
          }
        }
        setStatus(this, this.dataset.savingMessage);
        save(attributesFor(this)).then(() => setStatus(this, '')).catch(() => setStatus(this, this.dataset.errorMessage));
      });
    }
  }
  customElements.define('alcyon-delivery-preference', DeliveryPreference);
  const gate = (event, button, form) => {
    if (button && replay.has(button)) return;
    if (!button && replay.has(form)) return;
    const host = hosts().find(item => item.dataset.formId === form?.id);
    if (!host) {
      if (form?.id === 'cart-notification-form') {
        event.preventDefault();
        event.stopImmediatePropagation();
        window.location.assign(window.routes?.cart_url || '/cart');
      }
      return;
    }
    const select = host.querySelector('[data-delivery-choice]');
    const currentAttributes = attributesFor(host);
    // Preserve the native click and any wholesale app checkout handlers after verified saving.
    if (codes[select.value] && savedAttributes && Object.entries(currentAttributes).every(([key, value]) => savedAttributes[key] === value)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (pending.has(form)) return;
    if (!codes[select.value]) {
      select.setAttribute('aria-invalid', 'true');
      setStatus(host, host.dataset.requiredMessage);
      select.focus();
      select.reportValidity();
      return;
    }
    select.setAttribute('aria-invalid', 'false');
    const attributes = attributesFor(host);
    clearTimeout(host.detailsTimer);
    latest = { preference: select.value, details: host.querySelector('[data-delivery-details]').value };
    pending.add(form);
    if (button) button.setAttribute('aria-busy', 'true');
    setStatus(host, host.dataset.savingMessage);
    save(attributes).then(() => {
      pending.delete(form);
      if (button) button.removeAttribute('aria-busy');
      setStatus(host, '');
      const target = button || form;
      replay.add(target);
      try {
        if (button) {
          replay.add(form);
          button.click();
        } else {
          form.requestSubmit();
        }
      } finally {
        replay.delete(target);
        replay.delete(form);
      }
    }).catch(() => {
      pending.delete(form);
      if (button) button.removeAttribute('aria-busy');
      setStatus(host, host.dataset.errorMessage);
    });
  };
  document.addEventListener('click', event => {
    const button = event.target.closest?.('button[name="checkout"], input[name="checkout"]');
    if (button) gate(event, button, button.form || document.getElementById(button.getAttribute('form')));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    if (event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(form.action || '')) {
      gate(event, event.submitter || null, form);
    }
  }, true);
})();

